<template>
  <a-modal
    :visible="open"
    :title="modalTitle"
    :confirmLoading="submitLoading"
    @ok="handleSubmit"
    @cancel="handleCancel"
    width="600px"
    :destroyOnClose="true"
  >
    <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
      <a-form-model-item :label="$t('业务积分.业务积分类型')" prop="categoryId">
        <a-select v-model="form.categoryId" :placeholder="$t('业务积分.请选择业务积分类型')" allowClear>
          <a-select-option v-for="item in categoryList" :key="item.id" :value="item.id">
            {{ item.categoryName }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
    </a-form-model>

    <a-alert type="info" show-icon style="margin-bottom: 16px;">
      <template slot="message">
        <div>{{ $t('业务积分.模板说明') }}</div>
        <div style="margin-top: 8px;">
          <a-button type="link" size="small" icon="download" @click="downloadTemplate">{{ $t('业务积分.下载模板') }}</a-button>
        </div>
      </template>
    </a-alert>

    <a-upload-dragger
      :beforeUpload="beforeUpload"
      :fileList="fileList"
      :remove="handleRemove"
      accept=".xls,.xlsx"
    >
      <p class="ant-upload-drag-icon">
        <a-icon type="inbox" />
      </p>
      <p class="ant-upload-text">{{ $t('通用.按钮.上传') }}</p>
      <p class="ant-upload-hint">支持 .xls、.xlsx 格式的Excel文件</p>
    </a-upload-dragger>

    <div v-if="importResult" style="margin-top: 16px;">
      <a-alert :type="importResult.errors && importResult.errors.length > 0 ? 'warning' : 'success'" show-icon>
        <template slot="message">
          <div>{{ $t('业务积分.导入完成') }} {{ importResult.total }} 条</div>
          <div>{{ $t('业务积分.成功') }}：{{ importResult.success }} 条</div>
          <div v-if="importResult.errors && importResult.errors.length > 0">
            {{ $t('业务积分.失败') }}：{{ importResult.errors.length }} 条
            <a @click="showErrors = !showErrors" style="margin-left: 8px;">
              {{ showErrors ? $t('业务积分.收起详情') : $t('业务积分.展开详情') }}
            </a>
            <div v-if="showErrors" style="margin-top: 8px; max-height: 200px; overflow-y: auto; text-align: left;">
              <div v-for="(err, idx) in importResult.errors" :key="idx">{{ err }}</div>
            </div>
          </div>
        </template>
      </a-alert>
    </div>

    <template slot="footer">
      <a-button key="back" @click="handleCancel">{{ $t('通用.按钮.取消') }}</a-button>
      <a-button key="submit" type="primary" :loading="submitLoading" @click="handleSubmit" :disabled="!uploadFile || !form.categoryId">
        {{ $t('业务积分.开始导入') }}
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { listCategory } from '@/api/point/category'
import { getToken } from '@/utils/auth'

export default {
  name: 'ImportPointScoreModal',
  data() {
    return {
      open: false,
      submitLoading: false,
      uploadFile: null,
      fileList: [],
      importResult: null,
      showErrors: false,
      categoryList: [],
      form: {
        categoryId: undefined
      },
      rules: {
        categoryId: [{ required: true, message: this.$t('业务积分.请选择业务积分类型'), trigger: 'change' }]
      },
      uploadUrl: process.env.VUE_APP_BASE_API + '/point/userScore/import',
      modalTitle: this.$t('业务积分.导入业务积分')
    }
  },
  methods: {
    openModal() {
      this.open = true
      this.fileList = []
      this.uploadFile = null
      this.importResult = null
      this.showErrors = false
      this.form = { categoryId: undefined }
      this.loadCategory()
    },
    loadCategory() {
      listCategory({}).then(response => {
        this.categoryList = response.data || []
      })
    },
    beforeUpload(file) {
      const isExcel = file.name.endsWith('.xls') || file.name.endsWith('.xlsx')
      if (!isExcel) {
        this.$message.error('只能上传 .xls 或 .xlsx 格式的Excel文件！')
        return false
      }
      this.uploadFile = file
      this.fileList = [file]
      return false
    },
    handleRemove() {
      this.uploadFile = null
      this.fileList = []
      this.importResult = null
    },
    async downloadTemplate() {
      try {
        const response = await fetch(process.env.VUE_APP_BASE_API + '/point/userScore/import/template', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + getToken() }
        })
        const blob = await response.blob()
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', '业务积分导入模板.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('下载模板失败')
      }
    },
    handleSubmit() {
      if (!this.form.categoryId) {
        this.$message.warning(this.$t('业务积分.请选择业务积分类型'))
        return
      }
      if (!this.uploadFile) {
        this.$message.warning('请先上传Excel文件')
        return
      }
      this.submitLoading = true
      const formData = new FormData()
      formData.append('file', this.uploadFile)
      formData.append('categoryId', this.form.categoryId)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', this.uploadUrl, true)
      xhr.setRequestHeader('Authorization', 'Bearer ' + getToken())
      xhr.onload = () => {
        this.submitLoading = false
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText)
          if (response.success) {
            this.importResult = response.data
            this.$emit('ok')
          } else {
            this.$message.error(response.msg || '导入失败')
          }
        } else {
          this.$message.error('导入失败，请重试')
        }
      }
      xhr.onerror = () => {
        this.submitLoading = false
        this.$message.error('网络错误，导入失败')
      }
      xhr.send(formData)
    },
    handleCancel() {
      this.open = false
      this.uploadFile = null
      this.fileList = []
      this.importResult = null
      this.showErrors = false
      this.form = { categoryId: undefined }
    }
  }
}
</script>
